<script setup lang="ts">
import { computed } from 'vue';
import { useCustomFieldProps } from '@satellite/composables/useCustomFieldProps';
import { useCustomFieldValidation } from '@satellite/composables/useCustomFieldValidation';
import { customFieldInputTestId } from '@satellite/helpers/customFieldTestId';

import type { CustomFieldProps } from '../types';
import CustomFieldInputBase from '../CustomFieldInputBase.vue';

/**
 * String field type - single line text input
 */
const {
  hideDetails = 'auto',
  displayLabel = true,
  persistentPlaceholder = true,
  required = undefined,
  ...props
} = defineProps<CustomFieldProps>();

const emit = defineEmits<{
  'update:model-value': [value: string];
  /** @deprecated Use update:model-value instead */
  // TODO: Remove this event after the migration is complete
  input: [value: string];
}>();

const { normalizedProps } = useCustomFieldProps(() => ({
  ...props,
  hideDetails,
  displayLabel,
  persistentPlaceholder,
  required
}));
const { rules } = useCustomFieldValidation(
  computed(() => ({ ...props.field, required: normalizedProps.required }))
);

function handleInput(value: string) {
  emit('update:model-value', value);
  emit('input', value);
}
</script>

<template>
  <custom-field-input-base
    :field="field"
    v-bind="normalizedProps"
    :data-testid="customFieldInputTestId(testId, field.label)">
    <v-text-field
      color="primary"
      density="comfortable"
      variant="outlined"
      :hide-details="normalizedProps.hideDetails"
      :model-value="modelValue"
      :persistent-placeholder="normalizedProps.persistentPlaceholder"
      :placeholder="field.placeholder"
      :readonly="normalizedProps.readOnly"
      :rules="rules"
      @update:model-value="handleInput" />
  </custom-field-input-base>
</template>
